import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Spinner } from '../../shared/ui/spinner';
import {
	AlertTriangle,
	ChevronDown,
	ChevronUp,
	Gamepad2,
	GraduationCap,
	MoreVertical,
	Pencil,
	PiggyBank,
	Pill,
	Plus,
	Power,
	PowerOff,
	Sofa,
	Trash2,
	Wrench,
	X,
} from 'lucide-react';
import styles from './Goals.module.css';
import coinsWithPlusIcon from '../../shared/assets/icons/coins-plus-icon.png';
import AddGoalModal from '../../components/goalModal/AddGoalModal';
import EditGoalModal from '../../components/goalModal/EditGoalModal/EditGoalModal';
import AddContributionModal from '../../components/goalModal/AddContribution/AddContributionModal';
import {
	activateGoal,
	addContributionToGoal,
	deactivateGoal,
	deleteContribution,
	deleteFinancialGoal,
	getGoalContributions,
	getGoalsRequest,
} from './api';
import {
	getLocalDateString,
	sortTransactionsNewestFirst,
} from '../../shared/lib/date/date-utils';

const getIconByCategory = category => {
	const iconMap = {
		education: GraduationCap,
		savings: PiggyBank,
		health: Pill,
		home: Sofa,
		entertainment: Gamepad2,
		renovation: Wrench,
		other: PiggyBank,
	};
	return iconMap[category?.toLowerCase()] || PiggyBank;
};

const cardColors = [
	'#F2F7FC',
	'#FFF9F9',
	'#EDEEFF',
	'#FFF8F6',
	'#F4EDFF',
	'#F3F6F5',
];

const getCardColorIndex = goalId => {
	if (!goalId) return 0;
	let idNum = 0;
	if (typeof goalId === 'number') {
		idNum = goalId;
	} else if (typeof goalId === 'string') {
		const numericMatch = goalId.match(/\d+/);
		if (numericMatch) {
			idNum = parseInt(numericMatch[0], 10);
		} else {
			idNum = goalId
				.split('')
				.reduce((acc, char) => acc + char.charCodeAt(0), 0);
		}
	}
	return Math.abs(idNum) % cardColors.length;
};

const getCurrencySymbol = currency => {
	const symbols = {
		USD: '$',
		EUR: '€',
		AMD: '֏',
		RUB: '₽',
	};
	return symbols[currency] || currency;
};

const getGoalImageUrl = imageUrl => {
	if (!imageUrl) return null;
	const API_BASE_URL = process.env.REACT_APP_API_URL;
	if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
		return imageUrl.replace(/\/api\/v1\//g, '/');
	}
	const baseUrl = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
	if (imageUrl.startsWith('/uploads/')) {
		return `${baseUrl}${imageUrl}`;
	}
	if (!imageUrl.startsWith('/')) {
		return `${baseUrl}/uploads/${imageUrl}`;
	}
	return `${baseUrl}${imageUrl}`;
};

const formatGoalDate = (dateString, locale = 'en-US') => {
	if (!dateString) return 'No deadline';
	try {
		const datePart = dateString.split('T')[0];
		const [year, month, day] = datePart.split('-').map(Number);

		const monthsMap = {
			'en-US': [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sep',
				'Oct',
				'Nov',
				'Dec',
			],
			hy: [
				'Հնվ',
				'Փտվ',
				'Մրտ',
				'Ապր',
				'Մյս',
				'Հնս',
				'Հլս',
				'Օգս',
				'Սպտ',
				'Հկտ',
				'Նմբ',
				'Դկտ',
			],
			ru: [
				'янв',
				'фев',
				'мар',
				'апр',
				'май',
				'июн',
				'июл',
				'авг',
				'сен',
				'окт',
				'ноя',
				'дек',
			],
		};

		const months = monthsMap[locale] || monthsMap['en-US'];
		return `${day} ${months[month - 1]} ${year}`;
	} catch (error) {
		console.error('Error formatting date:', error);
		return 'No deadline';
	}
};

const Goals = () => {
	const intl = useIntl();
	const [goals, setGoals] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [activeMenu, setActiveMenu] = useState(null);
	const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isContributionModalOpen, setIsContributionModalOpen] = useState(false);
	const [selectedGoal, setSelectedGoal] = useState(null);
	const [contributionLoading, setContributionLoading] = useState(false);
	const [deleteConfirmGoal, setDeleteConfirmGoal] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [expandedGoalId, setExpandedGoalId] = useState(null);
	const [contributions, setContributions] = useState([]);
	const [contributionsLoading, setContributionsLoading] = useState(false);
	const [deleteContributionConfirm, setDeleteContributionConfirm] =
		useState(null);
	const [deleteContributionLoading, setDeleteContributionLoading] =
		useState(false);

	const menuRefs = useRef({});
	const contributionsSectionRef = useRef(null);

	useEffect(() => {
		fetchGoals();
	}, []);

	const getGoalType = goal => {
		if (goal.is_default !== undefined) {
			return goal.is_default
				? intl.formatMessage({ id: 'GoalsID31' })
				: intl.formatMessage({ id: 'GoalsID32' });
		}
		return (
			goal.type || goal.category || intl.formatMessage({ id: 'GoalsID31' })
		);
	};

	const fetchGoals = async () => {
		try {
			setLoading(true);
			const response = await getGoalsRequest();
			if (response && response.goals && Array.isArray(response.goals)) {
				const formattedGoals = response.goals.map((goal, index) => {
					return {
						id: goal.id || goal._id || `goal-${index}`,
						title: goal.title || 'Goal',
						type: getGoalType(goal),
						targetAmount:
							parseFloat(goal.target_amount) ||
							parseFloat(goal.targetAmount) ||
							0,
						savedAmount:
							parseFloat(goal.current_amount) ||
							parseFloat(goal.savedAmount) ||
							0,
						deadline: formatGoalDate(goal.due_date, intl.locale),
						status: goal.is_completed
							? intl.formatMessage({ id: 'GoalsID35' })
							: goal.is_active === false
								? intl.formatMessage({ id: 'GoalsID34' })
								: intl.formatMessage({ id: 'GoalsID33' }),
						originalData: goal,
					};
				});
				setGoals(formattedGoals);
			} else if (
				response &&
				response.data &&
				response.data.goals &&
				Array.isArray(response.data.goals)
			) {
				const formattedGoals = response.data.goals.map((goal, index) => ({
					id: goal.id || goal._id || `goal-${index}`,
					title: goal.title || 'Goal',
					icon: getIconByCategory(goal.category || goal.type),
					type: getGoalType(goal),
					targetAmount:
						parseFloat(goal.target_amount) ||
						parseFloat(goal.targetAmount) ||
						0,
					savedAmount:
						parseFloat(goal.current_amount) ||
						parseFloat(goal.savedAmount) ||
						0,
					deadline: formatGoalDate(goal.due_date, intl.locale),
					status: goal.is_completed
						? intl.formatMessage({ id: 'GoalsID35' })
						: goal.is_active === false
							? intl.formatMessage({ id: 'GoalsID34' })
							: intl.formatMessage({ id: 'GoalsID33' }),
					originalData: goal,
				}));
				setGoals(formattedGoals);
			} else if (Array.isArray(response)) {
				const formattedGoals = response.map((goal, index) => ({
					id: goal.id || goal._id || `goal-${index}`,
					title: goal.title || 'Goal',
					icon: getIconByCategory(goal.category || goal.type),
					type: getGoalType(goal),
					targetAmount:
						parseFloat(goal.target_amount) ||
						parseFloat(goal.targetAmount) ||
						0,
					savedAmount:
						parseFloat(goal.current_amount) ||
						parseFloat(goal.savedAmount) ||
						0,
					deadline: formatGoalDate(goal.due_date, intl.locale),
					status: goal.is_completed
						? intl.formatMessage({ id: 'GoalsID35' })
						: goal.is_active === false
							? intl.formatMessage({ id: 'GoalsID34' })
							: intl.formatMessage({ id: 'GoalsID33' }),
					originalData: goal,
				}));
				setGoals(formattedGoals);
			} else if (response && Array.isArray(response.data)) {
				const formattedGoals = response.data.map((goal, index) => ({
					id: goal.id || goal._id || index + 1,
					title: goal.title || goal.name || 'Goal',
					icon: getIconByCategory(goal.category || goal.type),
					type: getGoalType(goal),
					targetAmount:
						parseFloat(goal.target_amount) ||
						parseFloat(goal.targetAmount) ||
						0,
					savedAmount:
						parseFloat(goal.current_amount) ||
						parseFloat(goal.savedAmount) ||
						0,
					deadline:
						formatGoalDate(goal.due_date, intl.locale) ||
						goal.deadline ||
						'No deadline',
					status: goal.is_completed
						? intl.formatMessage({ id: 'GoalsID35' })
						: goal.is_active === false
							? intl.formatMessage({ id: 'GoalsID34' })
							: intl.formatMessage({ id: 'GoalsID33' }),
					originalData: goal,
				}));
				setGoals(formattedGoals);
			} else {
				console.error('Unexpected response format:', response);
				setGoals([]);
			}
			setError(null);
		} catch (err) {
			console.error('Error fetching goals:', err);
			setError(intl.formatMessage({ id: 'GoalsErrorID22' }));
			setGoals([]);
		} finally {
			setLoading(false);
		}
	};

	const handleMenuToggle = goalId => {
		if (activeMenu === goalId) {
			setActiveMenu(null);
		} else {
			const menuContainer = menuRefs.current[goalId];
			if (menuContainer) {
				const rect = menuContainer.getBoundingClientRect();
				setMenuPosition({
					top: rect.bottom + 8,
					right: window.innerWidth - rect.right,
				});
			}
			setActiveMenu(goalId);
		}
	};

	const handleEditGoal = goal => {
		setSelectedGoal(goal);
		setIsEditModalOpen(true);
		setActiveMenu(null);
	};

	const handleDeleteGoal = goalId => {
		const goalToDelete = goals.find(g => g.id === goalId);
		if (goalToDelete) {
			setDeleteConfirmGoal(goalToDelete);
			setActiveMenu(null);
		}
	};

	const handleConfirmDelete = async () => {
		if (!deleteConfirmGoal) return;
		setDeleteLoading(true);
		try {
			const backendGoalId =
				deleteConfirmGoal.originalData?.id || deleteConfirmGoal.id;
			await deleteFinancialGoal(backendGoalId);
			setDeleteConfirmGoal(null);
			fetchGoals();
		} catch (err) {
			console.error('Error deleting goal:', err);
			alert(err.message || intl.formatMessage({ id: 'GoalsErrorID23' }));
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmGoal(null);
	};

	const handleActivateGoal = async goalId => {
		try {
			const goalToActivate = goals.find(g => g.id === goalId);
			if (!goalToActivate) {
				console.error('Goal not found:', goalId);
				return;
			}
			const backendGoalId = goalToActivate.originalData?.id || goalId;
			await activateGoal(backendGoalId);
			fetchGoals();
			setActiveMenu(null);
		} catch (err) {
			console.error('Error activating goal:', err);
			alert(err.message || intl.formatMessage({ id: 'GoalsErrorID24' }));
		}
	};

	const handleDeactivateGoal = async goalId => {
		try {
			const goalToDeactivate = goals.find(g => g.id === goalId);
			if (!goalToDeactivate) {
				console.error('Goal not found:', goalId);
				return;
			}
			const backendGoalId = goalToDeactivate.originalData?.id || goalId;
			await deactivateGoal(backendGoalId);
			fetchGoals();
			setActiveMenu(null);
		} catch (err) {
			console.error('Error deactivating goal:', err);
			alert(err.message || intl.formatMessage({ id: 'GoalsErrorID25' }));
		}
	};

	const handleAddContribution = goalId => {
		const goalToContribute = goals.find(g => g.id === goalId);
		if (goalToContribute) {
			setSelectedGoal(goalToContribute);
			setIsContributionModalOpen(true);
		}
		setActiveMenu(null);
	};

	const handleSubmitContribution = async contributionData => {
		if (!selectedGoal) return;
		setContributionLoading(true);
		try {
			const backendGoalId = selectedGoal.originalData?.id || selectedGoal.id;
			const contributionRequestData = {
				amount: contributionData.amount,
				date: contributionData.date || getLocalDateString(),
				description: contributionData.description || '',
			};

			await addContributionToGoal(backendGoalId, contributionRequestData);
			const contributedGoalId = selectedGoal.id;
			setIsContributionModalOpen(false);
			setSelectedGoal(null);
			fetchGoals();
			if (expandedGoalId === contributedGoalId) {
				fetchContributionsForGoal(contributedGoalId);
			}
		} catch (error) {
			console.error('Error adding contribution:', error);
			alert(error.message || intl.formatMessage({ id: 'GoalsErrorID26' }));
		} finally {
			setContributionLoading(false);
		}
	};

	const fetchContributionsForGoal = async goalId => {
		const backendGoalId =
			goals.find(g => g.id === goalId)?.originalData?.id || goalId;
		setContributionsLoading(true);
		try {
			const data = await getGoalContributions(backendGoalId);
			const rows = Array.isArray(data) ? data : [];
			setContributions(
				sortTransactionsNewestFirst(
					rows.map(c => ({
						...c,
						transactionDate: c.date,
						createdAt: c.created_at,
					})),
				),
			);
		} catch (err) {
			console.error('Error fetching contributions:', err);
			setContributions([]);
		} finally {
			setContributionsLoading(false);
		}
	};

	const handleToggleContributions = goalId => {
		if (expandedGoalId === goalId) {
			setExpandedGoalId(null);
			setContributions([]);
		} else {
			setExpandedGoalId(goalId);
			fetchContributionsForGoal(goalId);
		}
	};

	useEffect(() => {
		if (expandedGoalId && contributionsSectionRef.current) {
			const el = contributionsSectionRef.current;
			const timer = setTimeout(() => {
				el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 150);
			return () => clearTimeout(timer);
		}
	}, [expandedGoalId]);

	const handleConfirmDeleteContribution = async () => {
		if (!deleteContributionConfirm) return;
		const { goalId, contributionId } = deleteContributionConfirm;
		const backendGoalId =
			goals.find(g => g.id === goalId)?.originalData?.id || goalId;
		setDeleteContributionLoading(true);
		try {
			await deleteContribution(backendGoalId, contributionId);
			setDeleteContributionConfirm(null);
			fetchContributionsForGoal(goalId);
			fetchGoals();
		} catch (err) {
			console.error('Error deleting contribution:', err);
			alert(err.message || intl.formatMessage({ id: 'GoalsErrorID27' }));
		} finally {
			setDeleteContributionLoading(false);
		}
	};

	const handleCancelDeleteContribution = () => {
		setDeleteContributionConfirm(null);
	};

	const getProgressPercentage = (saved, target) => {
		if (!target || target === 0) return 0;
		return Math.round((saved / target) * 100);
	};

	const getProgressColor = percentage => {
		if (percentage >= 100) return '#4ade80';
		if (percentage >= 50) return '#4ade80';
		return '#4ade80';
	};

	if (loading) {
		return <Spinner />;
	}

	if (error) {
		return (
			<div className={styles.container}>
				<div className={styles.errorContainer}>
					<p className={styles.errorText}>{error}</p>
					<button onClick={fetchGoals} className={styles.retryButton}>
						{intl.formatMessage({ id: 'GoalsID2' })}
					</button>
				</div>
			</div>
		);
	}

	// Find the active goal for portal dropdown rendering
	const activeGoal = activeMenu ? goals.find(g => g.id === activeMenu) : null;

	return (
		<div className={styles.container}>
			{goals.length === 0 ? (
				<div className={styles.emptyState}>
					<div className={styles.AllemptyState}>
						<h1>{intl.formatMessage({ id: 'GoalsID3' })}</h1>
						<p className={styles.goalText}>
							{intl.formatMessage({ id: 'GoalsID4' })}
						</p>
						<p className={styles.goaltext}>
							{intl.formatMessage({ id: 'GoalsID5' })}
							<br />
							{intl.formatMessage({ id: 'GoalsID6' })}
						</p>
						<div className={styles.emptyStateSection}>
							<button
								className={styles.emptyAddGoalButton}
								onClick={() => setIsAddModalOpen(true)}
							>
								<span>{intl.formatMessage({ id: 'GoalsID7' })}</span>
								<Plus size={20} style={{ paddingBottom: 2 }} />
							</button>
						</div>
					</div>
				</div>
			) : (
				<div className={styles.content}>
					<div className={styles.headerSection}>
						<button
							className={styles.addGoalButton}
							onClick={() => setIsAddModalOpen(true)}
						>
							<span>{intl.formatMessage({ id: 'GoalsID8' })}</span>
							<Plus size={20} style={{ paddingBottom: 2 }} />
						</button>
					</div>

					<div className={styles.goalsGrid}>
						{goals.map(goal => {
							const Icon = goal.icon;
							const percentage = getProgressPercentage(
								goal.savedAmount,
								goal.targetAmount,
							);
							const colorIndex = getCardColorIndex(goal.id);
							const cardColor = cardColors[colorIndex] || cardColors[0];
							const goalImageUrl = goal.originalData?.image_url
								? getGoalImageUrl(goal.originalData.image_url)
								: null;

							return (
								<div
									key={goal.id}
									className={styles.goalCard}
									style={{
										backgroundColor: goalImageUrl ? 'transparent' : cardColor,
										backgroundImage: goalImageUrl
											? `url(${goalImageUrl})`
											: 'none',
									}}
								>
									{goalImageUrl && <div className={styles.cardOverlay} />}
									<div className={styles.cardContent}>
										<div className={styles.cardHeader}>
											<div className={styles.goalTitle}>
												{Icon && <Icon size={24} className={styles.goalIcon} />}
												<span>{goal.title}</span>
											</div>
											<div className={styles.cardActions}>
												<span
													className={`${styles.typeBadge} ${
														goal.type === 'Primary'
															? styles.primaryBadge
															: styles.secondaryBadge
													}`}
												>
													{goal.type}
												</span>
												<div
													className={styles.menuContainer}
													ref={el => {
														if (el) menuRefs.current[goal.id] = el;
													}}
												>
													<button
														className={styles.menuButton}
														onClick={() => handleMenuToggle(goal.id)}
													>
														<MoreVertical size={20} />
													</button>
												</div>
											</div>
										</div>

										<div className={styles.amountSection}>
											<span className={styles.targetAmount}>
												{goal.targetAmount}
												{getCurrencySymbol(
													goal.originalData?.currency || 'USD',
												)}
											</span>
										</div>

										<div className={styles.progressSection}>
											<div className={styles.progressBar}>
												<div
													className={styles.progressFill}
													style={{
														width: `${Math.min(percentage, 100)}%`,
														backgroundColor: getProgressColor(percentage),
													}}
												/>
											</div>
										</div>

										<div className={styles.savedSection}>
											<span className={styles.savedText}>
												{goal.savedAmount}
												{getCurrencySymbol(
													goal.originalData?.currency || 'USD',
												)}
												{intl.formatMessage({ id: 'GoalsID14' })}
											</span>
											<span
												className={styles.percentageText}
												style={{ color: getProgressColor(percentage) }}
											>
												{percentage}%
											</span>
										</div>

										<button
											type='button'
											className={styles.myContributionsToggle}
											onClick={() => handleToggleContributions(goal.id)}
										>
											{expandedGoalId === goal.id ? (
												<ChevronUp size={18} />
											) : (
												<ChevronDown size={18} />
											)}
											<span>{intl.formatMessage({ id: 'GoalsID28' })}</span>
										</button>

										<div className={styles.deadlineSection}>
											<div>
												<span className={styles.deadlineLabel}>
													{intl.formatMessage({ id: 'GoalsID15' })}
												</span>
												<span className={styles.deadlineDate}>
													{goal.deadline}
												</span>
											</div>
											<span
												className={`${styles.statusBadge} ${
													goal.status ===
													intl.formatMessage({ id: 'GoalsID35' })
														? styles.completedBadge
														: goal.status ===
															  intl.formatMessage({ id: 'GoalsID34' })
															? styles.inactiveBadge
															: styles.activeBadge
												}`}
											>
												{goal.status}
											</span>
										</div>
									</div>
								</div>
							);
						})}
					</div>

					{expandedGoalId && (
						<div
							ref={contributionsSectionRef}
							className={styles.contributionsSection}
						>
							<h3 className={styles.contributionsSectionTitle}>
								{intl.formatMessage({ id: 'GoalsID22' })}{' '}
								{goals.find(g => g.id === expandedGoalId)?.title}
							</h3>
							{contributionsLoading ? (
								<div className={styles.contributionsLoading}>
									<div className={styles.spinner} />
									<span>{intl.formatMessage({ id: 'GoalsID23' })}</span>
								</div>
							) : (
								<div className={styles.tableContainer}>
									<div
										className={styles.tableHeader}
										style={{ gridTemplateColumns: '1fr 1fr 2fr 80px' }}
									>
										<div className={styles.tableHeaderCell}>
											{intl.formatMessage({ id: 'GoalsID24' })}
										</div>
										<div className={styles.tableHeaderCell}>
											{intl.formatMessage({ id: 'GoalsID25' })}
										</div>
										<div className={styles.tableHeaderCell}>
											{intl.formatMessage({ id: 'GoalsID26' })}
										</div>
										<div className={styles.tableHeaderCell} />
									</div>
									<div className={styles.tableBody}>
										{contributions.length === 0 ? (
											<div className={styles.contributionsEmpty}>
												{intl.formatMessage({ id: 'GoalsID27' })}
											</div>
										) : (
											contributions.map(c => (
												<div
													key={c.id}
													className={styles.tableRow}
													style={{ gridTemplateColumns: '1fr 1fr 2fr 80px' }}
												>
													<div className={styles.tableCell}>
														{/* {formatGoalDate(
                              typeof c.date === "string"
                                ? c.date
                                : c.date?.split?.("T")?.[0],
                            )} */}

														{formatGoalDate(
															typeof c.date === 'string'
																? c.date
																: c.date?.split?.('T')?.[0],
															intl.locale,
														)}
													</div>
													<div className={styles.tableCell}>
														{Number(c.amount)}{' '}
														{getCurrencySymbol(
															goals.find(g => g.id === expandedGoalId)
																?.originalData?.currency || 'USD',
														)}
													</div>
													<div className={styles.tableCell}>
														{c.description || '—'}
													</div>
													<div className={styles.tableCell}>
														<button
															type='button'
															className={styles.tableDeleteButton}
															onClick={() =>
																setDeleteContributionConfirm({
																	goalId: expandedGoalId,
																	contributionId: c.id,
																})
															}
															title={intl.formatMessage({ id: 'GoalsID13' })}
														>
															<Trash2 size={18} />
														</button>
													</div>
												</div>
											))
										)}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			)}

			{/* ── Portal: Backdrop ── */}
			{activeMenu && (
				<div
					className={styles.menuBackdrop}
					onClick={() => setActiveMenu(null)}
				/>
			)}

			{/* ── Portal: Dropdown menu ── */}
			{activeMenu && activeGoal && (
				<div
					className={styles.dropdownMenu}
					style={{
						position: 'fixed',
						top: menuPosition.top,
						right: menuPosition.right,
						zIndex: 10001,
					}}
				>
					<button
						className={styles.closeMenuButton}
						onClick={() => setActiveMenu(null)}
					>
						<X size={16} />
					</button>

					<button
						className={styles.menuItem}
						onClick={() => handleEditGoal(activeGoal)}
					>
						<Pencil size={18} className={styles.editIcon} />
						<span>{intl.formatMessage({ id: 'GoalsID9' })}</span>
					</button>

					<button
						className={`${styles.menuItem} ${
							activeGoal.status === intl.formatMessage({ id: 'GoalsID34' }) ||
							activeGoal.originalData?.is_active === false
								? styles.menuItemDisabled
								: ''
						}`}
						onClick={() => {
							if (
								activeGoal.status !== intl.formatMessage({ id: 'GoalsID34' }) &&
								activeGoal.originalData?.is_active !== false
							) {
								handleAddContribution(activeGoal.id);
							}
						}}
						disabled={
							activeGoal.status === intl.formatMessage({ id: 'GoalsID34' }) ||
							activeGoal.originalData?.is_active === false
						}
					>
						<img
							src={coinsWithPlusIcon}
							alt='Add Contribution'
							className={styles.coinsWithPlusIcon}
						/>
						<span>{intl.formatMessage({ id: 'GoalsID10' })}</span>
					</button>

					{activeGoal.originalData?.is_active ? (
						<button
							className={styles.menuItem}
							onClick={() => handleDeactivateGoal(activeGoal.id)}
						>
							<PowerOff size={18} className={styles.editIcon} />
							<span>{intl.formatMessage({ id: 'GoalsID11' })}</span>
						</button>
					) : (
						<button
							className={styles.menuItem}
							onClick={() => handleActivateGoal(activeGoal.id)}
						>
							<Power size={18} className={styles.editIcon} />
							<span>{intl.formatMessage({ id: 'GoalsID12' })}</span>
						</button>
					)}

					<button
						className={`${styles.menuItem} ${styles.deleteItem}`}
						onClick={() => handleDeleteGoal(activeGoal.id)}
					>
						<Trash2 size={18} className={styles.deleteIcon} />
						<span>{intl.formatMessage({ id: 'GoalsID13' })}</span>
					</button>
				</div>
			)}

			{/* Delete Goal Confirmation Modal */}
			{deleteConfirmGoal && (
				<div
					className={styles.deleteConfirmOverlay}
					onClick={handleCancelDelete}
				>
					<div
						className={styles.deleteConfirmModal}
						onClick={e => e.stopPropagation()}
					>
						<AlertTriangle size={48} className={styles.deleteWarningIcon} />
						<h3 className={styles.deleteConfirmTitle}>
							{intl.formatMessage({ id: 'GoalsID16' })}
						</h3>
						<p className={styles.deleteConfirmMessage}>
							{intl.formatMessage({ id: 'GoalsID17' })} "
							{deleteConfirmGoal.title}"{' '}
							{intl.formatMessage({ id: 'GoalsID18' })}
						</p>
						<div className={styles.deleteConfirmButtons}>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDelete}
								disabled={deleteLoading}
							>
								{deleteLoading
									? intl.formatMessage({ id: 'GoalsID19' })
									: intl.formatMessage({ id: 'GoalsID20' })}
							</button>
							<button
								className={styles.deleteCancelButton}
								onClick={handleCancelDelete}
								disabled={deleteLoading}
							>
								{intl.formatMessage({ id: 'GoalsID21' })}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Delete Contribution Confirmation Modal */}
			{deleteContributionConfirm && (
				<div
					className={styles.deleteConfirmOverlay}
					onClick={handleCancelDeleteContribution}
				>
					<div
						className={styles.deleteConfirmModal}
						onClick={e => e.stopPropagation()}
					>
						<AlertTriangle size={48} className={styles.deleteWarningIcon} />
						<h3 className={styles.deleteConfirmTitle}>
							{intl.formatMessage({ id: 'GoalsID29' })}
						</h3>
						<p className={styles.deleteConfirmMessage}>
							{intl.formatMessage({ id: 'GoalsID30' })}
						</p>
						<div className={styles.deleteConfirmButtons}>
							<button
								className={styles.deleteConfirmButton}
								onClick={handleConfirmDeleteContribution}
								disabled={deleteContributionLoading}
							>
								{deleteContributionLoading
									? intl.formatMessage({ id: 'GoalsID19' })
									: intl.formatMessage({ id: 'GoalsID20' })}
							</button>
							<button
								className={styles.deleteCancelButton}
								onClick={handleCancelDeleteContribution}
								disabled={deleteContributionLoading}
							>
								{intl.formatMessage({ id: 'GoalsID21' })}
							</button>
						</div>
					</div>
				</div>
			)}

			<AddGoalModal
				open={isAddModalOpen}
				onClose={() => setIsAddModalOpen(false)}
				onGoalAdded={fetchGoals}
			/>

			{selectedGoal && (
				<>
					<EditGoalModal
						open={isEditModalOpen}
						onClose={() => {
							setIsEditModalOpen(false);
							setSelectedGoal(null);
						}}
						onGoalUpdated={fetchGoals}
						onGoalDeleted={fetchGoals}
						goal={selectedGoal}
					/>

					<AddContributionModal
						isOpen={isContributionModalOpen}
						onClose={() => {
							setIsContributionModalOpen(false);
							setSelectedGoal(null);
						}}
						goalName={selectedGoal.title}
						goalCurrency={selectedGoal.originalData?.currency || 'USD'}
						onSubmit={handleSubmitContribution}
					/>
				</>
			)}
		</div>
	);
};

export default Goals;
